export enum ProgramType {
  doePr = 'doePr',
  energyBackup = 'energyBackup',
  generator = 'generator',
  geothermal = 'geothermal',
  hvac = 'hvac',
  newCommunity = 'newCommunity',
  newHomes = 'newHomes',
  consumerSolar = 'consumerSolar',
  solar = 'solar',
  waterHeater = 'waterHeater',
}

/**
 * Program types whose AVL lives in `appSettings` as a manufacturer allow-list
 * (one doc per program type, `value.<equipmentType>` = array of manufacturer entries).
 * These are NOT in the first-class `approvedVendorList` collection.
 */
export const MANUFACTURER_LIST_PROGRAM_TYPES: Set<ProgramType> = new Set<ProgramType>([
  ProgramType.geothermal,
  ProgramType.waterHeater,
  ProgramType.generator,
]);

export const isManufacturerListProgramType = (programType: ProgramType): boolean =>
  MANUFACTURER_LIST_PROGRAM_TYPES.has(programType);

export const ComfortPlanProgramTypes: ProgramType[] = [
  ProgramType.hvac,
  ProgramType.energyBackup,
  ProgramType.generator,
  ProgramType.geothermal,
  ProgramType.waterHeater,
];

export const StateLevelProgramTypes: ProgramType[] = [
  ProgramType.hvac,
  ProgramType.waterHeater,
  ProgramType.geothermal,
  ProgramType.generator,
];

export const CustomerSignOffProgramTypes: ProgramType[] = [
  ProgramType.hvac,
  ProgramType.waterHeater,
  ProgramType.geothermal,
  ProgramType.generator,
];

export const ItcEligibilityProgramTypes: ProgramType[] = [
  ProgramType.solar,
  ProgramType.newHomes,
  ProgramType.newCommunity,
];

/**
 * Program types that require an active utility for downstream pricing, quote creation, and
 * contract send. For these program types an inactive (or missing) utility must hard-fail at
 * pricing, quote, and contract send time — otherwise contracts would be signed against a
 * utility that Lightreach no longer supports.
 *
 * Note: account creation intentionally remains tolerant of an unknown/inactive utility (the
 * legacy `'Unknown Utility'` fallback in `getUtilityName` is preserved). The gates apply
 * later in the lifecycle so internal operations can still create accounts for ingestion /
 * triage purposes.
 */
export const UtilityRequiredProgramTypes: ProgramType[] = [
  ProgramType.solar,
  ProgramType.consumerSolar,
  ProgramType.doePr,
  ProgramType.newCommunity,
  ProgramType.newHomes,
  ProgramType.energyBackup,
];

export const programTypeRequiresUtility = (programType: ProgramType): boolean =>
  UtilityRequiredProgramTypes.includes(programType);
export const ProgramTypes: ProgramType[] = Object.values(ProgramType);
export const ProgramTypeLabels: Record<ProgramType, string> = {
  doePr: 'DOE Puerto Rico',
  energyBackup: 'Energy Backup',
  generator: 'Generator',
  geothermal: 'Ground Source Heat Pump',
  hvac: 'HVAC',
  newCommunity: 'New Community',
  newHomes: 'New Homes',
  consumerSolar: 'Consumer Solar',
  solar: 'Solar',
  waterHeater: 'Water Heater',
};

/** Any program type that involves solar panels */
export type SolarProgramTypes =
  | ProgramType.doePr
  | ProgramType.newCommunity
  | ProgramType.newHomes
  | ProgramType.consumerSolar
  | ProgramType.solar;

/** Does the program type involve solar panels? */
export const isSolarProgramType = (programType: ProgramType): boolean => {
  switch (programType) {
    case ProgramType.solar:
    case ProgramType.newHomes:
    case ProgramType.doePr:
    case ProgramType.newCommunity:
    case ProgramType.consumerSolar:
      return true;
    case ProgramType.energyBackup:
    case ProgramType.generator:
    case ProgramType.geothermal:
    case ProgramType.hvac:
    case ProgramType.waterHeater:
      return false;
    default:
      // Still exhaustive at compile time — a new ProgramType fails to build here — but returning
      // instead of throwing keeps a not-yet-loaded `programType` from taking the page down.
      programType satisfies never;
      return false;
  }
};

/**
 * The solar-family program types as a list, for route allow-lists and other places that need
 * to enumerate rather than test. Derived from `isSolarProgramType` so a new ProgramType only
 * has to be classified once, in that switch.
 */
export const SolarProgramTypeList: readonly ProgramType[] = Object.freeze(ProgramTypes.filter(isSolarProgramType));

/** The solar program types that solar pricing supports. newCommunity is not priced yet (see getProductPricing). */
export const SolarPricingProgramTypeList: readonly ProgramType[] = Object.freeze(
  SolarProgramTypeList.filter((programType) => programType !== ProgramType.newCommunity),
);

export const programTypesToAllowBillingRegardlessIfTranched: ProgramType[] = [ProgramType.doePr];

// The last milestone package page where flags can be applied to an account
export const LAST_MILESTONE_PACKAGE_BY_PROGRAM_TYPE: Partial<Record<ProgramType, number>> = {
  newCommunity: 3,
  newHomes: 3,
  consumerSolar: 4,
  solar: 3,
  hvac: 3,
  energyBackup: 3,
  generator: 3,
  geothermal: 3,
  doePr: 4,
  waterHeater: 3,
};
